import React from 'react';
import { useHistory } from 'react-router-dom';
import styled, { CSSProperties } from 'styled-components';

import IconButton from '@material-ui/core/IconButton/IconButton';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import Typography from '@material-ui/core/Typography/Typography';

import { icons } from '../themes/icons';
import { FlexRow } from './GlobalStyled/GlobalStyled';
import { LoadingBackdrop } from './LoadingBackdrop';

interface Props {
  addUrl?: string;
  backLabel?: string;
  backTo?: string;
  loading?: boolean;
  title?: string;
  subTitle?: string;
  titleNode?: React.ReactNode;
  titleButtons?: React.ReactNode;
  showFilterTitle?: string;
  titleStyle?: CSSProperties;
  toggleShowFilter?: () => void;
  mobileTitleHeader?: boolean;
  offerEidtPage?: boolean;
  agencyDetailPage?: boolean;
}

export const PageWrapper: React.FC<Props> = ({
  addUrl,
  backTo,
  children,
  loading,
  showFilterTitle,
  title,
  titleNode,
  subTitle,
  titleButtons,
  titleStyle,
  mobileTitleHeader,
  toggleShowFilter,
  offerEidtPage,
  agencyDetailPage,
}) => {
  const history = useHistory();

  const onBackClick = React.useCallback(() => {
    if (history.length > 2) {
      history.goBack();
    } else if (backTo) {
      history.push(backTo);
    }
  }, []);

  const addTitle = addUrl ? 'Add New ' + title?.split(' ')[0].replace(/s$/g, '') : undefined;
  const onAddClick = React.useCallback(() => (addUrl ? history.push(addUrl) : undefined), [addUrl]);

  return (
    <PageContent offerEidtPage={offerEidtPage} agencyDetailPage={agencyDetailPage}>
      {title && (
        <PageTitleWrapper
          extraStyle={titleStyle}
          className={mobileTitleHeader ? 'mobile_title_header' : ''}
        >
          {backTo && (
            <BackButton color={titleStyle?.color} onClick={onBackClick}>
              <icons.KeyboardBackspaceIcon fontSize="large" />
            </BackButton>
          )}
          <PageTitle>
            <Typography color="textPrimary" gutterBottom variant="h5" align="left">
              {title}
            </Typography>
            {titleNode && titleNode}
            {subTitle && (
              <SubTitle color="textPrimary" variant="body1" align="left">
                {subTitle}
              </SubTitle>
            )}
          </PageTitle>
          {addTitle && (
            <Tooltip title={addTitle}>
              <IconButton onClick={onAddClick} size="small">
                <icons.AddIcon />
              </IconButton>
            </Tooltip>
          )}
          {showFilterTitle && (
            <Tooltip title={showFilterTitle}>
              <IconButton onClick={toggleShowFilter} size="small">
                <icons.EqualizerIcon />
              </IconButton>
            </Tooltip>
          )}
          <TitleButtons>{titleButtons}</TitleButtons>
        </PageTitleWrapper>
      )}
      <PageChildren>{children}</PageChildren>
      <LoadingBackdrop loading={loading} />
    </PageContent>
  );
};

const BackButton = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1rem;
  transform: scale(1.6);
  text-decoration: none;
  color: ${({ theme, color }) => color || theme.colors.brandMediumGrey};
  cursor: pointer;

  :hover {
    color: ${({ theme, color }) => color || theme.colors.brandBlue};
  }

  svg {
    height: 18px;
    margin-right: 4px;
  }
`;

const PageContent = styled.div<{ offerEidtPage?: boolean; agencyDetailPage?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  position: relative;
  padding: 0.5rem 1rem 0;
  /* 'clip' instead of 'hidden' so we still suppress horizontal overflow
     without turning this element into a scroll container — that would
     otherwise break 'position: sticky' on descendants (they'd attach to
     this box instead of the viewport). */
  overflow-x: clip;
  .mobile_title_header {
    min-height: 80px !important;
  }

  @media (max-width: 487px) {
    height: ${(props) => (props.offerEidtPage ? '90%' : '')};

    padding: ${(props) => (props.agencyDetailPage ? '0' : '0.5rem 1rem 0')};
  }
`;

const PageTitle = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0.25rem 0;

  h5 {
    font-weight: 500;
  }

  @media screen and (max-width: 1024px) {
    h5 {
      font-size: 1.2rem;
    }
  }
`;

const SubTitle = styled(Typography)`
  display: flex;
  flex-direction: row;
  margin-left: 16px;
  align-items: center;
`;

const PageTitleWrapper = styled('div')<{ extraStyle?: CSSProperties }>`
  display: flex;
  flex-flow: row wrap-reverse;
  align-items: center;
  margin-bottom: 0rem;
  min-height: 40px;

  h5 {
    margin-bottom: 0;
  }

  @media (max-width: 487px) {
    padding: 0 0.3rem;
  }
  ${({ extraStyle }) => ({ ...extraStyle })}
`;

const PageChildren = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TitleButtons = styled(FlexRow)`
  flex: 1 1 auto;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: nowrap;
  margin-right: -0.25rem;
`;
